/**
 * PayslipLinePresetPicker — Dropdown pour sélectionner un libellé préconfiguré
 * (prime ou retenue) et l'insérer dans la fiche de paie en cours.
 *
 * L'utilisateur peut :
 *  - Cliquer sur un libellé existant → ajoute une ligne pré-remplie
 *  - Cliquer sur "Autre (saisir librement)" → ajoute une ligne vide
 *  - Cliquer sur "+ Nouveau libellé permanent…" → crée un preset via API et l'ajoute
 *  - Cliquer sur le "×" à côté d'un libellé personnalisé → le supprime définitivement
 */
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { Plus, Sparkles, X, Wand2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (v) => (Math.round(v || 0)).toLocaleString("fr-FR");

/**
 * @param {Object} props
 * @param {"bonus"|"deduction"} props.kind
 * @param {Function} props.onAddLine - reçoit { label, amount }
 * @param {string} props.color - couleur d'accent ("emerald" | "rose")
 */
const PayslipLinePresetPicker = ({ kind, onAddLine, color = "emerald" }) => {
  const [presets, setPresets] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/salaries/line-presets`, { params: { kind } });
      setPresets(res.data || []);
    } catch (e) {
      /* ignore */
    } finally { setLoading(false); }
  }, [kind]);
  useEffect(() => { load(); }, [load]);

  const pick = (preset) => {
    onAddLine?.({ label: preset.label, amount: preset.default_amount || 0 });
  };
  const pickBlank = () => onAddLine?.({ label: "", amount: "" });

  const createNew = async () => {
    const label = window.prompt(
      kind === "bonus"
        ? "Nouveau libellé de prime (sera enregistré pour les prochaines fiches) :"
        : "Nouveau libellé de retenue (sera enregistré pour les prochaines fiches) :",
      ""
    );
    if (!label || !label.trim()) return;
    const amtStr = window.prompt("Montant par défaut (F CFA) — laissez 0 si variable :", "0");
    const amount = parseFloat(String(amtStr || "0").replace(",", ".")) || 0;
    try {
      const res = await axios.post(`${API}/salaries/line-presets`, {
        kind, label: label.trim(), default_amount: amount,
      });
      toast.success(`Libellé « ${res.data.label} » enregistré`);
      await load();
      onAddLine?.({ label: res.data.label, amount: res.data.default_amount || 0 });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const deletePreset = async (preset, ev) => {
    ev?.stopPropagation?.();
    ev?.preventDefault?.();
    if (!window.confirm(`Supprimer définitivement le libellé « ${preset.label} » ?`)) return;
    try {
      await axios.delete(`${API}/salaries/line-presets/${preset.id}`);
      toast.success("Libellé supprimé");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const accentClasses = color === "rose"
    ? "text-rose-400 hover:text-rose-300"
    : "text-emerald-400 hover:text-emerald-300";

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={`text-[11.5px] font-bold flex items-center gap-1 ${accentClasses}`}
          data-testid={`payslip-add-${kind}`}
        >
          <Plus className="w-3 h-3" strokeWidth={2.6} /> Ajouter
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="bg-slate-800 border-slate-700 text-white w-64 max-h-[380px] overflow-y-auto z-[10001]"
      >
        <DropdownMenuLabel className="text-slate-400 text-[10px] uppercase tracking-wider font-bold flex items-center gap-1">
          <Sparkles className="w-3 h-3" /> Libellés préconfigurés
        </DropdownMenuLabel>
        {loading ? (
          <div className="py-3 text-center text-slate-500 text-[11px]">Chargement…</div>
        ) : presets.length === 0 ? (
          <div className="py-3 text-center text-slate-500 text-[11px] italic">Aucun libellé enregistré</div>
        ) : presets.map((p) => (
          <DropdownMenuItem
            key={p.id}
            onSelect={() => pick(p)}
            className="text-slate-200 hover:bg-slate-700/70 focus:bg-slate-700/70 cursor-pointer text-[12px] flex items-center gap-2 pr-1"
            data-testid={`preset-${p.id}`}
          >
            <div className="flex-1 min-w-0">
              <div className="font-semibold truncate">{p.label}</div>
              {p.default_amount > 0 && (
                <div className="text-slate-500 text-[10px] font-mono">Défaut : {fmt(p.default_amount)} F</div>
              )}
            </div>
            {!p.system && (
              <button
                type="button"
                onClick={(e) => deletePreset(p, e)}
                className="w-5 h-5 rounded flex-shrink-0 hover:bg-rose-500/20 text-slate-500 hover:text-rose-300 flex items-center justify-center"
                title="Supprimer ce libellé"
              >
                <X className="w-3 h-3" />
              </button>
            )}
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator className="bg-slate-700" />
        <DropdownMenuItem
          onSelect={pickBlank}
          className="text-slate-300 hover:bg-slate-700/70 focus:bg-slate-700/70 cursor-pointer text-[12px]"
        >
          <Plus className="w-3 h-3 mr-2" /> Autre (saisir librement)
        </DropdownMenuItem>
        <DropdownMenuItem
          onSelect={createNew}
          className="text-blue-300 hover:bg-slate-700/70 focus:bg-slate-700/70 cursor-pointer text-[12px]"
          data-testid={`preset-new-${kind}`}
        >
          <Wand2 className="w-3 h-3 mr-2" /> Nouveau libellé permanent…
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default PayslipLinePresetPicker;
